import { useState, useRef } from "react";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Send, Upload, Download, FolderOpen, Search } from "lucide-react";
import { toast } from "sonner";

const DOC_LABELS = {
  lab_results: "Lab Results",
  photo_id: "Photo ID",
  insurance_card: "Insurance",
  prescriber_notes: "Prescriber Notes",
  other: "Other",
};

/**
 * Prescriber "Patient Documents" tab — two-pane UI with patient list on the left
 * and that patient's document history on the right. Supports:
 *   - Upload (prescriber_notes) directly to a patient's record
 *   - Download individual docs
 *   - "Request Document" modal that POSTs to `/document-requests` so the patient
 *     is prompted (in-app + email) to upload the requested document
 *
 * Own state (previously — buggily — referenced from the parent dashboard scope):
 *   `reqPatientSearch` filters patients inside the request dialog.
 */
export default function PrescriberDocumentsTab({ patients }) {
  const [selectedPatientId, setSelectedPatientId] = useState("");
  const [patientDocs, setPatientDocs] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [reqForm, setReqForm] = useState({ patient_id: "", document_type: "lab_results", message: "" });
  const [reqPatientSearch, setReqPatientSearch] = useState("");
  const [patientListSearch, setPatientListSearch] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  // Filter the left-pane patient list by first/last name or DOB (accepts ISO YYYY-MM-DD or US MM/DD/YYYY)
  const filteredPatients = (() => {
    const q = patientListSearch.trim().toLowerCase();
    if (!q) return patients;
    return patients.filter((p) => {
      const name = (p.name || "").toLowerCase();
      const dobIso = (p.date_of_birth || "").toLowerCase();
      let dobUS = "";
      if (dobIso && dobIso.length >= 10) {
        const [y, m, d] = dobIso.slice(0, 10).split("-");
        if (y && m && d) dobUS = `${m}/${d}/${y}`;
      }
      return name.includes(q) || dobIso.startsWith(q) || dobUS.startsWith(q);
    });
  })();

  const loadPatientDocs = async (pid) => {
    setSelectedPatientId(pid);
    setLoadingDocs(true);
    try {
      const { data } = await API.get(`/documents/patient/${pid}`);
      setPatientDocs(data);
    } catch { setPatientDocs([]); } finally { setLoadingDocs(false); }
  };

  const handleRequest = async () => {
    if (!reqForm.patient_id) { toast.error("Select a patient"); return; }
    try {
      await API.post("/document-requests", reqForm);
      toast.success("Document request sent to patient");
      setShowRequestModal(false);
      setReqPatientSearch("");
    } catch { toast.error("Failed to send request"); }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedPatientId) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      await API.post(
        `/documents/upload?document_type=prescriber_notes&patient_id=${selectedPatientId}&description=Prescriber upload`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      toast.success("Document uploaded to patient record");
      loadPatientDocs(selectedPatientId);
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const handleDownload = async (doc) => {
    try {
      const response = await API.get(`/documents/download/${doc.id}`, { responseType: "blob" });
      const url = URL.createObjectURL(response.data);
      const a = document.createElement("a");
      a.href = url; a.download = doc.original_filename; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  return (
    <div data-testid="prescriber-documents-tab">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>Patient Documents</h1>
        <Button
          size="sm"
          className="bg-purple-600 hover:bg-purple-700 text-slate-900 dark:text-white rounded-lg"
          onClick={() => { setShowRequestModal(true); setReqForm({ patient_id: "", document_type: "lab_results", message: "" }); setReqPatientSearch(""); }}
          data-testid="request-doc-btn"
        >
          <Send className="w-4 h-4 mr-1" /> Request Document
        </Button>
      </div>

      <div className="grid lg:grid-cols-4 gap-6">
        {/* Patient list */}
        <div className="lg:col-span-1 bg-slate-100 dark:bg-slate-800 rounded-xl border border-slate-300 dark:border-slate-700 p-4 max-h-[600px] overflow-y-auto">
          <p className="text-xs font-bold uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-3">Select Patient</p>
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 dark:text-slate-500 pointer-events-none" />
            <Input
              value={patientListSearch}
              onChange={(e) => setPatientListSearch(e.target.value)}
              placeholder="Search name or DOB"
              className="pl-9 h-9 text-sm rounded-lg bg-white dark:bg-slate-900"
              data-testid="doc-patient-list-search"
            />
          </div>
          {filteredPatients.map(p => (
            <button
              key={p.id}
              onClick={() => loadPatientDocs(p.id)}
              className={`w-full text-left px-3 py-2 rounded-lg text-sm mb-1 transition-colors ${selectedPatientId === p.id ? "bg-purple-50 text-purple-700 font-medium" : "text-slate-600 dark:text-slate-300 hover:bg-slate-800/50"}`}
              data-testid={`doc-patient-${p.id}`}
            >
              <p className="truncate">{p.name}</p>
              {p.date_of_birth && (
                <p className="text-[11px] text-slate-400 dark:text-slate-500">DOB {(() => { const [y,m,d] = (p.date_of_birth||"").slice(0,10).split("-"); return y && m && d ? `${m}/${d}/${y}` : p.date_of_birth; })()}</p>
              )}
            </button>
          ))}
          {patients.length === 0 && <p className="text-xs text-slate-400 dark:text-slate-500">No patients</p>}
          {patients.length > 0 && filteredPatients.length === 0 && <p className="text-xs text-slate-400 dark:text-slate-500">No matches</p>}
        </div>

        {/* Documents panel */}
        <div className="lg:col-span-3">
          {!selectedPatientId ? (
            <div className="bg-slate-100 dark:bg-slate-800 rounded-xl border border-slate-300 dark:border-slate-700 p-12 text-center">
              <FolderOpen className="w-10 h-10 text-slate-600 dark:text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 dark:text-slate-400">Select a patient to view documents</p>
            </div>
          ) : loadingDocs ? (
            <div className="text-center py-12 text-slate-400 dark:text-slate-500">Loading documents...</div>
          ) : (
            <div>
              <div className="flex items-center justify-between mb-4">
                <p className="text-sm text-slate-600 dark:text-slate-300">{patientDocs.length} document(s)</p>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" className="rounded-lg" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="prescriber-upload-btn">
                    <Upload className="w-4 h-4 mr-1" /> {uploading ? "Uploading..." : "Upload to Record"}
                  </Button>
                  <input ref={fileRef} type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.doc,.docx" onChange={handleUpload} />
                </div>
              </div>
              {patientDocs.length === 0 ? (
                <div className="bg-slate-100 dark:bg-slate-800 rounded-xl border border-slate-300 dark:border-slate-700 p-8 text-center">
                  <p className="text-slate-400 dark:text-slate-500">No documents for this patient</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {patientDocs.map(doc => (
                    <div key={doc.id} className="bg-slate-100 dark:bg-slate-800 rounded-xl border border-slate-300 dark:border-slate-700 p-4 flex items-center justify-between">
                      <div>
                        <p className="font-medium text-sm text-slate-800 dark:text-slate-100">{doc.original_filename}</p>
                        <div className="flex items-center gap-2 text-xs text-slate-400 dark:text-slate-500">
                          <span className="px-2 py-0.5 rounded-full bg-purple-50 text-purple-700">{DOC_LABELS[doc.document_type] || doc.document_type}</span>
                          <span>{(doc.size / 1024).toFixed(0)} KB</span>
                          <span>{new Date(doc.created_at).toLocaleDateString()}</span>
                          <span>by {doc.uploaded_by_name} ({doc.uploaded_by_role})</span>
                        </div>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => handleDownload(doc)} data-testid={`prescriber-download-${doc.id}`}>
                        <Download className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Request document modal */}
      <Dialog open={showRequestModal} onOpenChange={() => { setShowRequestModal(false); setReqPatientSearch(""); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle style={{ fontFamily: "'Outfit', sans-serif" }}>Request Document from Patient</DialogTitle></DialogHeader>
          <div className="space-y-4" data-testid="request-doc-modal">
            <div>
              <Label className="text-sm font-medium text-slate-700 dark:text-slate-200">Find Patient</Label>
              <div className="relative mt-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 dark:text-slate-500 pointer-events-none" />
                <Input
                  value={reqPatientSearch}
                  onChange={(e) => setReqPatientSearch(e.target.value)}
                  placeholder="Search name or DOB (MM/DD/YYYY)"
                  className="pl-9 rounded-xl"
                  data-testid="req-patient-search"
                />
              </div>
              {reqPatientSearch.trim() && (
                <div className="mt-2 max-h-48 overflow-y-auto border border-slate-200 dark:border-slate-800 rounded-xl divide-y divide-slate-100 dark:divide-slate-800/60" data-testid="req-patient-results">
                  {(() => {
                    const q = reqPatientSearch.trim().toLowerCase();
                    const matches = patients.filter((p) => {
                      const name = (p.name || "").toLowerCase();
                      const dobIso = p.date_of_birth || "";
                      let dobUS = "";
                      if (dobIso && dobIso.length >= 10) {
                        const [y, m, d] = dobIso.slice(0, 10).split("-");
                        if (y && m && d) dobUS = `${m}/${d}/${y}`;
                      }
                      return name.includes(q) || dobIso.startsWith(q) || dobUS.startsWith(q);
                    }).slice(0, 20);
                    if (matches.length === 0) return <p className="p-3 text-sm text-slate-500 dark:text-slate-400">No patients match</p>;
                    return matches.map((p) => {
                      const selected = reqForm.patient_id === p.id;
                      return (
                        <button
                          key={p.id}
                          type="button"
                          onClick={() => { setReqForm({ ...reqForm, patient_id: p.id }); setReqPatientSearch(p.name); }}
                          className={`w-full text-left px-3 py-2 text-sm transition-colors ${selected ? "bg-purple-50 text-purple-900" : "hover:bg-slate-50 dark:hover:bg-slate-800/50 dark:bg-slate-800/40 text-slate-700 dark:text-slate-200"}`}
                          data-testid={`req-patient-pick-${p.id}`}
                        >
                          <p className="font-medium">{p.name}</p>
                          <p className="text-xs text-slate-500 dark:text-slate-400">{p.email} · DOB {p.date_of_birth || "—"}</p>
                        </button>
                      );
                    });
                  })()}
                </div>
              )}
              {reqForm.patient_id && !reqPatientSearch.trim() && (
                <p className="mt-2 text-xs text-purple-700">Selected patient ID: <span className="font-mono">{reqForm.patient_id.slice(0, 10)}</span></p>
              )}
            </div>
            <div>
              <Label className="text-sm font-medium text-slate-700 dark:text-slate-200">Document Type</Label>
              <Select value={reqForm.document_type} onValueChange={(v) => setReqForm({ ...reqForm, document_type: v })}>
                <SelectTrigger className="mt-1 rounded-xl" data-testid="req-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="lab_results">Lab Results</SelectItem>
                  <SelectItem value="photo_id">Photo ID</SelectItem>
                  <SelectItem value="insurance_card">Insurance Card</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-medium text-slate-700 dark:text-slate-200">Message to Patient</Label>
              <Textarea value={reqForm.message} onChange={(e) => setReqForm({ ...reqForm, message: e.target.value })} placeholder="e.g. Please upload your latest A1C lab results" className="mt-1 rounded-xl" data-testid="req-message-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowRequestModal(false); setReqPatientSearch(""); }}>Cancel</Button>
            <Button className="bg-purple-600 hover:bg-purple-700 text-slate-900 dark:text-white" onClick={handleRequest} disabled={!reqForm.patient_id} data-testid="send-doc-request-btn">
              <Send className="w-4 h-4 mr-1" /> Send Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
